import { createAsyncThunk } from '@reduxjs/toolkit';
import api from '@/utils/api';

export interface Message {
  id: string;
  from: {
    name: string;
    avatar?: string;
    username: string;
  };
  lastMessage: string;
  timestamp: Date;
  unread: boolean;
}

export interface MessageThread {
  id: string;
  participants: Array<{
    id: string;
    name: string;
    username: string;
    avatar?: string;
    /** Server-driven bot flag (see docs/klyx_bots.md). Drives the
     *  "🤖 Bot" chip on messages from a bot counterparty. */
    isBot?: boolean;
  }>;
  lastMessage: {
    id: string;
    content: string;
    senderId: string;
    timestamp: string;
    isSpotlighted?: boolean;
  };
  unreadCount: number;
  updatedAt: string;
}

export interface MessagesResponse {
  threads: MessageThread[];
  unreadTotal: number;
}

// Fetch message threads (conversations list)
export const fetchMessageThreads = createAsyncThunk(
  'messages/fetchThreads',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get<MessagesResponse>('/messages/threads', {
        params: { limit: 20 }
      });
      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to fetch messages';
      return rejectWithValue(message);
    }
  }
);

// Fetch messages in a specific thread
export const fetchThreadMessages = createAsyncThunk(
  'messages/fetchThread',
  async (threadId: string, { rejectWithValue }) => {
    try {
      const response = await api.get(`/messages/threads/${threadId}`);
      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to fetch thread';
      return rejectWithValue(message);
    }
  }
);

// Send a message (with optional spotlight)
export const sendMessage = createAsyncThunk(
  'messages/send',
  async ({
    threadId,
    content,
    spotlight
  }: {
    threadId: string;
    content: string;
    spotlight?: {
      enabled: boolean;
      txHash?: string; // For wallet login
    };
  }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/messages/threads/${threadId}/messages`, {
        content,
        spotlight
      });
      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.message || error.response?.data?.error || 'Failed to send message';
      return rejectWithValue(message);
    }
  }
);

// Mark messages as read
export const markMessagesAsRead = createAsyncThunk(
  'messages/markRead',
  async (threadId: string, { rejectWithValue }) => {
    try {
      const response = await api.patch(`/messages/threads/${threadId}/read`);
      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to mark as read';
      return rejectWithValue(message);
    }
  }
);

// Get unread message count
export const fetchUnreadCount = createAsyncThunk(
  'messages/unreadCount',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get<{ unreadCount: number }>('/messages/unread-count');
      return response.data.unreadCount;
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to fetch unread count';
      return rejectWithValue(message);
    }
  }
);

// Fetch recipient's DM spotlight fee from blockchain
export const fetchRecipientSpotlightFee = createAsyncThunk(
  'messages/fetchSpotlightFee',
  async (recipientId: string, { rejectWithValue }) => {
    try {
      // Get creator's profile fees directly (combined endpoint)
      const response = await api.get(`/blockchain-profiles/creator/${recipientId}/fees`);

      if (!response.data.success || !response.data.fees) {
        return rejectWithValue('Failed to fetch spotlight fee');
      }

      return {
        fee: response.data.fees.dmSpotlightFee || '10',
        hasProfile: true
      };
    } catch (error: any) {
      const message = error.response?.data?.message || error.response?.data?.error || 'Failed to fetch spotlight fee';
      return rejectWithValue(message);
    }
  }
);
